/**
 * WhatsApp Button Component
 *
 * Dropdown button with template options for sending WhatsApp messages.
 * Used in patient table and patient detail sheet.
 */

import * as React from 'react'
import { MessageCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  normalizePhoneForWhatsApp,
  getWhatsAppUrl,
  isValidWhatsAppNumber,
} from '@/lib/whatsapp'
import {
  WHATSAPP_TEMPLATES,
  applyTemplateVariables,
} from './templates'

interface WhatsAppButtonProps {
  phone: string | null | undefined
  patientName: string
  branchName: string | null | undefined
  /** Compact mode for table cells */
  compact?: boolean
  /** Additional CSS classes */
  className?: string
}

export function WhatsAppButton({
  phone,
  patientName,
  branchName,
  compact = false,
  className,
}: WhatsAppButtonProps) {
  const normalizedPhone = normalizePhoneForWhatsApp(phone)
  const isValid = isValidWhatsAppNumber(phone)

  // Handle template selection
  const handleTemplateClick = (templateId: string | null) => {
    if (!normalizedPhone) return

    let message: string | undefined

    if (templateId) {
      const template = WHATSAPP_TEMPLATES.find((t) => t.id === templateId)
      if (template) {
        message = applyTemplateVariables(template.message, {
          name: patientName,
          branch: branchName || 'kami',
        })
      }
    }

    const url = getWhatsAppUrl(normalizedPhone, message)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Disabled state - no valid phone
  if (!isValid) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size={compact ? 'icon' : 'default'}
              className={`${compact ? 'h-8 w-8' : ''} opacity-40 cursor-not-allowed ${className}`}
              disabled
            >
              <MessageCircle className={compact ? 'h-4 w-4' : 'mr-2 h-4 w-4'} style={{ color: '#25D366' }} />
              {!compact && <span>WhatsApp</span>}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>No valid phone number</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size={compact ? 'icon' : 'default'}
                className={`${compact ? 'h-8 w-8' : ''} hover:bg-green-500/10 ${className}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MessageCircle
                  className={compact ? 'h-4 w-4' : 'mr-2 h-4 w-4'}
                  style={{ color: '#25D366' }}
                  fill="#25D366"
                />
                {!compact && <span>WhatsApp</span>}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Send WhatsApp message</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[13px] flex items-center gap-2">
          <MessageCircle className="h-4 w-4" style={{ color: '#25D366' }} />
          Send WhatsApp
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Quick message - no template */}
        <DropdownMenuItem
          onClick={() => handleTemplateClick(null)}
          className="text-[13px] cursor-pointer"
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Quick Message
          <span className="ml-auto text-xs text-muted-foreground">No template</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] text-muted-foreground font-normal">
          Templates
        </DropdownMenuLabel>

        {/* Template options */}
        {WHATSAPP_TEMPLATES.map((template) => (
          <DropdownMenuItem
            key={template.id}
            onClick={() => handleTemplateClick(template.id)}
            className="text-[13px] cursor-pointer"
          >
            <span className="mr-2">{template.icon}</span>
            {template.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Simple WhatsApp icon button (no dropdown)
 * For use when you just want quick access without templates
 */
export function WhatsAppIconButton({
  phone,
  className,
}: {
  phone: string | null | undefined
  className?: string
}) {
  const normalizedPhone = normalizePhoneForWhatsApp(phone)
  const isValid = isValidWhatsAppNumber(phone)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!normalizedPhone) return
    const url = getWhatsAppUrl(normalizedPhone)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${isValid ? 'hover:bg-green-500/10' : 'opacity-40 cursor-not-allowed'} ${className}`}
            onClick={handleClick}
            disabled={!isValid}
          >
            <MessageCircle
              className="h-4 w-4"
              style={{ color: '#25D366' }}
              fill={isValid ? '#25D366' : undefined}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isValid ? 'Open WhatsApp' : 'No valid phone number'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
